import { createEffect, createSignal, type JSX } from "solid-js";
import type { Row, NavHunk } from "../nav-hunks";
import type { TokensByLine, Token, ScopeName } from "@fastdiff/highlight";
import type { FontSize } from "../stores/view";

export interface MinimapProps {
  rows: readonly Row[];
  viewportTop: () => number;
  viewportHeight: () => number;
  totalContentHeight: () => number;
  navHunks: readonly NavHunk[];
  currentHunkIndex: () => number;
  tokens?: { left?: TokensByLine; right?: TokensByLine };
  fontSize: () => FontSize;
  onJumpTo: (scrollTop: number) => void;
  onWheel?: (deltaY: number) => void;
  onTimingMs?: (ms: number) => void;
}

const MINIMAP_WIDTH = 60;
const PIXEL_THRESHOLD = 5000;
const PIXEL_LINE_HEIGHT = 3;

// Pixel-mode layout constants
const PX_COL_WIDTH = 28;  // each column inside 60px minimap
const PX_GAP = 4;
const PX_CHAR_W = 1;
const PX_BG_DEL = "rgba(240,90,90,0.18)";
const PX_BG_INS = "rgba(80,200,120,0.18)";

type Mode = "pixel" | "stripe";

// Map the ScopeName union to a smaller set of color buckets
type ScopeBucket = "keyword" | "string" | "comment" | "number" | "type" | "function" | "plain";

function classifyScope(scope: ScopeName): ScopeBucket {
  switch (scope) {
    case "keyword":   return "keyword";
    case "string":
    case "regex":     return "string";
    case "comment":   return "comment";
    case "number":
    case "constant":  return "number";
    case "type":      return "type";
    case "function":  return "function";
    default:          return "plain";
  }
}

function resolveTokenColors(host: HTMLElement): Record<ScopeBucket, string> {
  const cs = getComputedStyle(host);
  const get = (name: string, fallback: string) => {
    const v = cs.getPropertyValue(name).trim();
    return v.length > 0 ? v : fallback;
  };
  return {
    keyword:  get("--fd-hl-keyword",  "#c586c0"),
    string:   get("--fd-hl-string",   "#ce9178"),
    comment:  get("--fd-hl-comment",  "#6a9955"),
    number:   get("--fd-hl-number",   "#b5cea8"),
    type:     get("--fd-hl-type",     "#4ec9b0"),
    function: get("--fd-hl-function", "#dcdcaa"),
    plain:    get("--fd-hl-plain",    "#d4d4d4"),
  };
}

function bgForKind(kind: string): string | null {
  if (kind === "delete" || kind === "change-left") return PX_BG_DEL;
  if (kind === "insert" || kind === "change-right") return PX_BG_INS;
  return null;
}

function drawTokenLine(
  ctx: CanvasRenderingContext2D,
  line: ReadonlyArray<Token>,
  baseX: number,
  y: number,
  maxWidth: number,
  colors: Record<ScopeBucket, string>,
) {
  for (const tok of line) {
    const start = tok.start;
    const end = Math.min(tok.end, maxWidth);
    if (end <= start) continue;
    const bucket = classifyScope(tok.scope);
    ctx.fillStyle = colors[bucket];
    ctx.fillRect(baseX + start * PX_CHAR_W, y, (end - start) * PX_CHAR_W, PIXEL_LINE_HEIGHT - 1);
  }
}

function drawPixel(
  ctx: CanvasRenderingContext2D,
  host: HTMLElement,
  props: MinimapProps,
) {
  const totalH = props.rows.length * PIXEL_LINE_HEIGHT;
  ctx.clearRect(0, 0, MINIMAP_WIDTH, totalH);
  const colors = resolveTokenColors(host);
  const leftX = 0;
  const rightX = PX_COL_WIDTH + PX_GAP;

  for (let i = 0; i < props.rows.length; i++) {
    const row = props.rows[i]!;
    const y = i * PIXEL_LINE_HEIGHT;

    // Row background for changed/deleted/inserted lines
    const lBg = bgForKind(row.left.kind);
    if (lBg) {
      ctx.fillStyle = lBg;
      ctx.fillRect(leftX, y, PX_COL_WIDTH, PIXEL_LINE_HEIGHT);
    }
    const rBg = bgForKind(row.right.kind);
    if (rBg) {
      ctx.fillStyle = rBg;
      ctx.fillRect(rightX, y, PX_COL_WIDTH, PIXEL_LINE_HEIGHT);
    }

    // Token pixels — sourceLine null means placeholder row (no tokens)
    const lLine = row.left.sourceLine != null ? props.tokens?.left?.[row.left.sourceLine] : undefined;
    const rLine = row.right.sourceLine != null ? props.tokens?.right?.[row.right.sourceLine] : undefined;
    if (lLine) drawTokenLine(ctx, lLine, leftX, y, PX_COL_WIDTH, colors);
    if (rLine) drawTokenLine(ctx, rLine, rightX, y, PX_COL_WIDTH, colors);
  }
}

function pickMode(props: MinimapProps): Mode {
  const tokens = props.tokens;
  const hasTokens =
    tokens != null &&
    (((tokens.left?.length ?? 0) > 0) || ((tokens.right?.length ?? 0) > 0));
  return props.rows.length <= PIXEL_THRESHOLD && hasTokens ? "pixel" : "stripe";
}

// HunkKind → fill color (CSS variables not available in Canvas; resolve via getComputedStyle).
function resolveColors(host: HTMLElement) {
  const cs = getComputedStyle(host);
  const get = (name: string, fallback: string) => {
    const v = cs.getPropertyValue(name).trim();
    return v.length > 0 ? v : fallback;
  };
  return {
    delete:  get("--fd-diff-del-bg", "rgba(240,90,90,0.6)"),
    insert:  get("--fd-diff-add-bg", "rgba(80,200,120,0.6)"),
    change:  get("--fd-diff-chg-bg", "rgba(235,200,60,0.6)"),
  };
}

export function Minimap(props: MinimapProps): JSX.Element {
  let host: HTMLDivElement | undefined;
  let canvas: HTMLCanvasElement | undefined;
  const [mode, setMode] = createSignal<Mode>(pickMode(props));
  const [canvasOffset, setCanvasOffset] = createSignal(0);

  const drawStripe = (ctx: CanvasRenderingContext2D, hostEl: HTMLElement, h: number) => {
    const c = resolveColors(hostEl);
    ctx.clearRect(0, 0, MINIMAP_WIDTH, h);
    const total = props.rows.length;
    if (total === 0) return;
    for (const nh of props.navHunks) {
      const top    = (nh.rowStart / total) * h;
      const height = Math.max(2, ((nh.rowEnd - nh.rowStart) / total) * h);
      ctx.fillStyle =
        nh.kind === "delete" ? c.delete :
        nh.kind === "insert" ? c.insert :
        c.change;
      ctx.fillRect(0, top, MINIMAP_WIDTH, height);
    }
  };

  const draw = () => {
    if (!canvas || !host) return;
    const t0 = performance.now();
    const m = pickMode(props);
    setMode(m);
    const minimapH = props.viewportHeight();
    if (m === "stripe") {
      canvas.width = MINIMAP_WIDTH;
      canvas.height = minimapH;
      canvas.style.height = `${minimapH}px`;
      canvas.dataset.mode = "stripe";
    } else {
      // pixel mode — 2-column token pixel rendering.
      canvas.width = MINIMAP_WIDTH;
      canvas.height = Math.max(1, props.rows.length * PIXEL_LINE_HEIGHT);
      canvas.style.height = `${canvas.height}px`;
      canvas.dataset.mode = "pixel";
    }
    const ctx = canvas.getContext("2d");
    if (ctx) {
      if (m === "stripe") drawStripe(ctx, host, minimapH);
      else drawPixel(ctx, host, props);
    }
    props.onTimingMs?.(performance.now() - t0);
  };

  createEffect(() => {
    // Track reactive deps so re-draws happen on row/token/viewport-size changes.
    props.rows;
    props.tokens;
    props.navHunks;
    props.fontSize();
    props.viewportHeight();
    queueMicrotask(draw);
  });

  // canvasOffset for pixel mode (used in Task 5+): driven by viewportTop.
  createEffect(() => {
    if (mode() !== "pixel" || !canvas) { setCanvasOffset(0); return; }
    const minimapH = props.viewportHeight();
    const canvasH = canvas.height;
    const totalH = props.totalContentHeight();
    if (canvasH <= minimapH || totalH <= props.viewportHeight()) {
      setCanvasOffset(0);
      return;
    }
    const ratio = props.viewportTop() / (totalH - props.viewportHeight());
    setCanvasOffset(Math.max(0, Math.min(canvasH - minimapH, ratio * (canvasH - minimapH))));
  });

  const computeScrollTop = (offsetY: number): number => {
    const m = mode();
    const minimapH = props.viewportHeight();
    const totalH = props.totalContentHeight();
    if (totalH <= 0 || minimapH <= 0) return 0;
    let ratio: number;
    if (m === "pixel") {
      const canvasH = props.rows.length * PIXEL_LINE_HEIGHT;
      if (canvasH <= 0) return 0;
      ratio = (offsetY + canvasOffset()) / canvasH;
    } else {
      ratio = offsetY / minimapH;
    }
    const targetCenter = ratio * totalH;
    return Math.max(0, targetCenter - props.viewportHeight() / 2);
  };

  let dragging = false;

  const hostY = (e: PointerEvent): number => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    return e.clientY - rect.top;
  };

  const onPointerDown = (e: PointerEvent) => {
    dragging = true;
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    props.onJumpTo(computeScrollTop(hostY(e)));
  };
  const onPointerMove = (e: PointerEvent) => {
    if (!dragging) return;
    props.onJumpTo(computeScrollTop(hostY(e)));
  };
  const onPointerUp = (e: PointerEvent) => {
    dragging = false;
    (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
  };

  let viewportEl: HTMLDivElement | undefined;
  let currentEl: HTMLDivElement | undefined;

  // viewport tracker — maps scroll ratio to minimap visible height
  createEffect(() => {
    if (!viewportEl) return;
    const totalH = props.totalContentHeight();
    if (totalH <= 0) {
      viewportEl.style.top = "0px";
      viewportEl.style.height = "0px";
      return;
    }
    const minimapH = props.viewportHeight();
    const ratioTop = props.viewportTop() / totalH;
    const ratioBottom = (props.viewportTop() + props.viewportHeight()) / totalH;
    viewportEl.style.top = `${ratioTop * minimapH}px`;
    viewportEl.style.height = `${Math.max(2, (ratioBottom - ratioTop) * minimapH)}px`;
  });

  // current hunk overlay — position depends on mode
  createEffect(() => {
    if (!currentEl) return;
    const nh = props.navHunks[props.currentHunkIndex()];
    const total = props.rows.length;
    if (!nh || total === 0) {
      currentEl.style.top = "0px";
      currentEl.style.height = "0px";
      return;
    }
    const m = mode();
    const minimapH = props.viewportHeight();
    const canvasBase = m === "pixel" ? props.rows.length * PIXEL_LINE_HEIGHT : minimapH;
    const offset = m === "pixel" ? canvasOffset() : 0;
    const computedH = ((nh.rowEnd - nh.rowStart) / total) * canvasBase;
    currentEl.style.top = `${(nh.rowStart / total) * canvasBase - offset}px`;
    currentEl.style.height = `${Math.max(2, computedH)}px`;
  });

  return (
    <div
      ref={(el) => { host = el; }}
      class="fd-minimap"
      style={{ width: `${MINIMAP_WIDTH}px`, height: `${props.viewportHeight()}px` }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onWheel={(e) => { if (props.onWheel) props.onWheel(e.deltaY); }}
    >
      <canvas
        ref={(el) => { canvas = el; }}
        style={{ transform: `translateY(${-canvasOffset()}px)` }}
      />
      <div ref={(el) => { viewportEl = el; }} class="fd-minimap-viewport" />
      <div ref={(el) => { currentEl = el; }} class="fd-minimap-current-hunk" />
    </div>
  );
}
